import { useRef, useState, type MouseEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { get, post } from '../api';
import { askConfirm } from '../components/ask';
import { Field, Problems } from '../components/Field';
import { UnsavedChanges } from '../components/UnsavedChanges';
import { currentLocale } from '../dates';
import { describe } from '../errors';
import { useLoad } from '../hooks';
import { formatMoney } from '../money';

interface RunClient {
  client_id: number;
  name: string;
  ready: number;
  amount: string;
  held: number;
  unfinished: number;
}
interface RunVisit {
  id: number;
  scheduled_start: string;
  scheduled_end: string;
  duration_minutes: number;
  charge_amount: string;
  customer_name: string | null;
  address_line1: string | null;
  locality: string | null;
  state: 'ready' | 'held' | 'unfinished';
}
interface Line {
  visit_id: number;
  description: string;
  amount: string;
  tax_rate: string;
}
interface Proposal {
  client_id: number;
  client_name: string;
  email: string;
  address: Record<string, string>;
  reference: string;
  terms: number;
  theme: string;
  lines: Line[];
}
interface Created {
  client_id: number;
  /** Who it was for, kept so the run can still name a client once it has nothing left to invoice. */
  name?: string;
  number: string;
  /** Its name here: Xero's number, else the business's own (INV-0012). */
  label?: string;
  invoice_id: number;
  xero_url?: string;
}

const cents = (x: string) => Math.round(Number(x || 0) * 100);
const day = (iso: string) =>
  new Date(iso).toLocaleDateString(currentLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
// A no-break space, so a duration never splits from its unit.
const hours = (minutes: number) => `${Math.round((minutes / 60) * 100) / 100} h`;
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
/** GST as the accounting system will add it: each line at its own rate. */
const gstOf = (lines: Line[]) =>
  lines.reduce((s, l) => s + Math.round((cents(l.amount) * Number(l.tax_rate || 0)) / 100), 0);

/** The body of POST /api/invoices for a proposal as it stands. */
const invoiceBody = (p: Proposal, visitIds: number[], remember: boolean) => ({
  visit_ids: visitIds,
  client_id: p.client_id,
  email: p.email,
  address: p.address,
  reference: p.reference,
  terms: p.terms,
  theme: p.theme,
  remember,
  lines: Object.fromEntries(
    p.lines
      .filter((l) => visitIds.includes(l.visit_id))
      .map((l) => [l.visit_id, { description: l.description, amount: l.amount }]),
  ),
});

/**
 * Invoice the finished work client by client: the app proposes each client's
 * invoice from their ready visits, the office checks it and creates it, then
 * moves to the next. Leaving part-way loses nothing: what is invoiced is gone
 * from the list, and the rest is still here next time.
 */
export function InvoiceRun() {
  const [params, setParams] = useSearchParams();
  const [tick, setTick] = useState(0);
  // Invoices made in this sitting, by client, so the list can say what was done.
  const [made, setMade] = useState<Created[]>([]);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string[]>([]);
  const run = useLoad(() => get<{ clients: RunClient[] }>('/api/invoice-run'), [tick]);
  const clients = run.data?.clients ?? [];
  const ready = clients.filter((c) => c.ready > 0);
  const chosen = Number(params.get('client')) || ready[0]?.client_id || clients[0]?.client_id || 0;
  const open = (id: number) => setParams({ client: String(id) });
  const rest = ready.filter((c) => c.client_id !== chosen);
  const total = ready.reduce((s, c) => s + cents(c.amount), 0) / 100;
  // A client invoiced in full leaves the server's list; the run keeps it, marked done.
  const listed = [
    ...clients,
    ...made
      .filter(
        (m, i) =>
          !clients.some((c) => c.client_id === m.client_id) &&
          made.findIndex((x) => x.client_id === m.client_id) === i,
      )
      .map((m) => ({
        client_id: m.client_id,
        name: m.name ?? '',
        ready: 0,
        amount: '0.00',
        held: 0,
        unfinished: 0,
      })),
  ];

  const created = (c: Created) => {
    setMade((m) => [...m, c]);
    setTick((t) => t + 1);
  };

  // The remaining clients' invoices exactly as proposed, one after another.
  async function createRest() {
    const amount = rest.reduce((s, c) => s + cents(c.amount), 0) / 100;
    const yes = await askConfirm({
      title: `Create the remaining ${plural(rest.length, 'invoice')}?`,
      body: `${rest.map((c) => c.name).join(', ')}: ${formatMoney(amount)} ex GST in all, each with every ready visit and the details as proposed.`,
      confirmLabel: `Create ${plural(rest.length, 'invoice')}`,
    });
    if (!yes) return;
    setBusy(true);
    setFailed([]);
    const problems: string[] = [];
    for (const c of rest) {
      try {
        const proposal = await get<{ visits: RunVisit[]; review: Proposal | null }>(
          `/api/invoice-run/${c.client_id}`,
        );
        if (!proposal.review) continue;
        const ids = proposal.visits.filter((v) => v.state === 'ready').map((v) => v.id);
        const res = await post<Created>('/api/invoices', invoiceBody(proposal.review, ids, true));
        created({ ...res, client_id: c.client_id, name: c.name });
      } catch (e) {
        problems.push(`${c.name}: ${describe(e).problems.join(' ')}`);
      }
    }
    setFailed(problems);
    setBusy(false);
  }

  return (
    <div className="stack">
      <p>
        <Link to="/money">← Money</Link>
      </p>
      <h1>Invoice run</h1>
      {run.error && (
        <p role="alert" className="error">
          {run.error}
        </p>
      )}
      {run.loading && !run.data && <p className="muted">Loading…</p>}
      {run.data && ready.length === 0 && made.length === 0 && (
        <div className="empty-state">
          <h2>Nothing to invoice</h2>
          <p>
            Every finished visit is on an invoice.
            {clients.length > 0 &&
              ' The visits below still need marking complete or releasing first.'}
          </p>
        </div>
      )}
      {run.data && ready.length === 0 && made.length > 0 && (
        <div className="empty-state" role="status">
          <h2>All invoiced</h2>
          <p>
            {plural(made.length, 'invoice')} created in this run. Every finished visit is now on an
            invoice.
          </p>
          <p>
            <Link to="/money?tab=invoices">See the invoices</Link>
          </p>
        </div>
      )}
      {ready.length > 0 && (
        <p className="muted" aria-live="polite">
          {plural(ready.length, 'client')} with visits ready · {formatMoney(total)} ex GST
        </p>
      )}
      <Problems problems={failed} title="Some invoices were not created." />
      {listed.length > 0 && (
        <div className="run">
          <nav className="run-clients" aria-label="Clients">
            <ul className="plain">
              {listed.map((c) => {
                const done = made.find((m) => m.client_id === c.client_id);
                return (
                  <li key={c.client_id}>
                    <button
                      type="button"
                      className={c.client_id === chosen ? 'run-client current' : 'run-client'}
                      aria-current={c.client_id === chosen ? 'true' : undefined}
                      onClick={() => open(c.client_id)}
                    >
                      <span className="run-client-name">{c.name || 'No client'}</span>
                      <span className="muted">
                        {c.ready > 0
                          ? `${plural(c.ready, 'visit')} · ${formatMoney(c.amount)}`
                          : done
                            ? `Invoiced${done.number || done.label ? ` · ${done.number || done.label}` : ''}`
                            : [
                                c.unfinished > 0 && `${c.unfinished} to complete`,
                                c.held > 0 && `${c.held} held back`,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          {chosen > 0 && (
            <ClientInvoice
              key={`${chosen}-${tick}`}
              clientId={chosen}
              name={
                clients.find((c) => c.client_id === chosen)?.name ??
                made.find((m) => m.client_id === chosen)?.name ??
                ''
              }
              made={[...made].reverse().find((m) => m.client_id === chosen)}
              next={rest[0]}
              // After the first invoice of the run, the rest can be made as proposed in one go.
              restCount={made.length > 0 ? rest.length : 0}
              busy={busy}
              onCreateRest={() => void createRest()}
              onCreated={(c) => {
                // Stay on this client, so the run says what was made before moving on.
                open(c.client_id);
                created(c);
              }}
              onChanged={() => setTick((t) => t + 1)}
              onNext={() => rest[0] && open(rest[0].client_id)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ClientInvoice(props: {
  clientId: number;
  name: string;
  made: Created | undefined;
  next: RunClient | undefined;
  restCount: number;
  busy: boolean;
  onCreateRest(): void;
  onCreated(c: Created): void;
  onChanged(): void;
  onNext(): void;
}) {
  const [again, setAgain] = useState(false);
  const loaded = useLoad(
    () =>
      get<{ visits: RunVisit[]; review: Proposal | null }>(`/api/invoice-run/${props.clientId}`),
    [props.clientId],
  );
  const [edited, setEdited] = useState<Proposal | null>(null);
  const [unticked, setUnticked] = useState<number[]>([]);
  const [remember, setRemember] = useState(true);
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const lastTicked = useRef<number | null>(null);

  if (loaded.loading && !loaded.data) return <p className="muted">Loading…</p>;
  if (!loaded.data) {
    return (
      <p role="alert" className="error">
        {loaded.error}
      </p>
    );
  }
  const { visits, review } = loaded.data;
  const p = edited ?? review;
  const ready = visits.filter((v) => v.state === 'ready');
  const unfinished = visits.filter((v) => v.state === 'unfinished');
  const held = visits.filter((v) => v.state === 'held');
  const picked = ready.map((v) => v.id).filter((id) => !unticked.includes(id));
  const lines = (p?.lines ?? []).filter((l) => picked.includes(l.visit_id));
  const net = lines.reduce((s, l) => s + cents(l.amount), 0);
  const gst = gstOf(lines);
  const changedTerms =
    !!review && !!p && (p.reference !== review.reference || p.terms !== review.terms);

  const set = (patch: Partial<Proposal>) => p && setEdited({ ...p, ...patch });
  const setLine = (id: number, patch: Partial<Line>) =>
    p &&
    setEdited({ ...p, lines: p.lines.map((l) => (l.visit_id === id ? { ...l, ...patch } : l)) });

  // Shift-click ticks or unticks every visit between this one and the last one clicked.
  function tick(e: MouseEvent<HTMLInputElement>, index: number) {
    const id = ready[index]!.id;
    const on = unticked.includes(id);
    const from = e.shiftKey && lastTicked.current !== null ? lastTicked.current : index;
    const span = ready.slice(Math.min(from, index), Math.max(from, index) + 1).map((v) => v.id);
    setUnticked((u) => (on ? u.filter((x) => !span.includes(x)) : [...new Set([...u, ...span])]));
    lastTicked.current = index;
  }

  async function act(run: () => Promise<unknown>, said: string) {
    setProblems([]);
    setMessage('');
    try {
      await run();
      setMessage(said);
      props.onChanged();
    } catch (e) {
      setProblems(describe(e).problems);
    }
  }

  async function create() {
    if (!p) return;
    setBusy(true);
    setProblems([]);
    setFields({});
    try {
      const res = await post<Created>('/api/invoices', invoiceBody(p, picked, remember));
      props.onCreated({ ...res, client_id: props.clientId, name: props.name || p.client_name });
    } catch (e) {
      const d = describe(e);
      setProblems(d.problems);
      setFields(d.fields);
      setBusy(false);
    }
  }

  if (props.made && !again) {
    return (
      <section className="run-client-invoice" aria-labelledby="run-h">
        <h2 id="run-h">{props.name}</h2>
        <p className="notice" role="status">
          {props.made.number
            ? `Draft ${props.made.number} created in Xero. Approve and send it to the client there.`
            : `Invoice ${props.made.label ?? ''} saved here as a draft. Print it and email it to the client from the invoice.`}{' '}
          <Link to={`/invoices/${props.made.invoice_id}`}>
            {props.made.number ? 'See the invoice' : 'Print and send it'}
          </Link>
          {props.made.xero_url && (
            <>
              {' · '}
              <a href={props.made.xero_url} target="_blank" rel="noopener noreferrer">
                Open in Xero<span className="sr-only"> (opens a new tab)</span>
              </a>
            </>
          )}
        </p>
        {ready.length > 0 && (
          <p>
            {plural(ready.length, 'visit')} you left off {ready.length === 1 ? 'is' : 'are'} still
            ready.{' '}
            <button type="button" className="quiet" onClick={() => setAgain(true)}>
              Invoice {ready.length === 1 ? 'it' : 'them'} too
            </button>
          </p>
        )}
        <div className="run-bar">
          <span aria-live="polite">
            {props.next
              ? `Next: ${props.next.name}`
              : 'That was the last client with visits ready.'}
          </span>
          {props.next && (
            <button type="button" onClick={props.onNext} disabled={props.busy}>
              Next client
            </button>
          )}
          {props.restCount > 1 && (
            <button
              type="button"
              className="quiet-button"
              disabled={props.busy}
              onClick={props.onCreateRest}
            >
              {props.busy ? 'Creating…' : `Create the remaining ${props.restCount} as proposed`}
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="run-client-invoice" aria-labelledby="run-h">
      <UnsavedChanges when={(edited !== null || unticked.length > 0) && !busy} />
      <h2 id="run-h">{p?.client_name ?? visits[0]?.customer_name ?? 'Client'}</h2>
      <Problems problems={problems} title="Not created." />
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}

      {p && (
        <>
          <fieldset>
            <legend>Details</legend>
            <Field
              label="Invoice email"
              hint="The client's own: a change here is saved to the client."
              error={fields.email}
            >
              <input value={p.email} onChange={(e) => set({ email: e.target.value })} />
            </Field>
            <Field label="Reference" error={fields.reference}>
              <input value={p.reference} onChange={(e) => set({ reference: e.target.value })} />
            </Field>
            <Field label="Payment terms (days)" error={fields.terms}>
              <input
                inputMode="numeric"
                value={p.terms}
                onChange={(e) => set({ terms: Number(e.target.value) || 0 })}
              />
            </Field>
            {changedTerms && (
              <label className="check">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />{' '}
                Also save the reference and terms for this client's next invoice
              </label>
            )}
          </fieldset>

          <div className="table-scroll">
            <table className="run-lines">
              <thead>
                <tr>
                  <th className="pick">
                    <input
                      type="checkbox"
                      aria-label={`Include all ${ready.length} visits`}
                      checked={unticked.length === 0}
                      onChange={() =>
                        setUnticked(unticked.length === 0 ? ready.map((v) => v.id) : [])
                      }
                    />
                  </th>
                  <th>Visit</th>
                  <th>On the invoice</th>
                  <th className="num">Amount ex GST</th>
                </tr>
              </thead>
              <tbody>
                {ready.map((v, i) => {
                  const line = p.lines.find((l) => l.visit_id === v.id);
                  const on = !unticked.includes(v.id);
                  const where = [v.customer_name, v.address_line1 || v.locality]
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <tr key={v.id} className={on ? undefined : 'left-out'}>
                      <td className="pick">
                        <input
                          type="checkbox"
                          aria-label={`Include ${where}, ${day(v.scheduled_start)}`}
                          checked={on}
                          onClick={(e) => tick(e, i)}
                          onChange={() => {}}
                        />
                      </td>
                      <td>
                        <Link to={`/visits/${v.id}`}>{day(v.scheduled_start)}</Link>
                        <div className="muted">
                          {where} · {hours(v.duration_minutes)}
                        </div>
                      </td>
                      <td>
                        {/* A textarea, so the whole line the client will read is in view. */}
                        <textarea
                          aria-label={`Description for ${day(v.scheduled_start)}`}
                          rows={2}
                          value={line?.description ?? ''}
                          disabled={!on}
                          onChange={(e) => setLine(v.id, { description: e.target.value })}
                        />
                      </td>
                      <td className="num">
                        <input
                          className="num short"
                          inputMode="decimal"
                          aria-label={`Amount for ${day(v.scheduled_start)}`}
                          aria-invalid={fields[`line_${v.id}`] ? true : undefined}
                          value={line?.amount ?? ''}
                          disabled={!on}
                          onChange={(e) => setLine(v.id, { amount: e.target.value })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {unfinished.length > 0 && (
        <details className="run-fold">
          <summary>{plural(unfinished.length, 'visit')} not completed yet</summary>
          <p className="muted">
            Their time has passed but nobody marked them complete, so they can't go on an invoice.
          </p>
          <ul className="plain">
            {unfinished.map((v) => (
              <li key={v.id} className="row start">
                <Link to={`/visits/${v.id}`}>
                  {day(v.scheduled_start)}, {v.customer_name}
                </Link>
                <button
                  type="button"
                  className="quiet"
                  onClick={() =>
                    void act(
                      () => post(`/api/visits/${v.id}/complete`),
                      `${v.customer_name ?? 'The visit'}, ${day(v.scheduled_start)}, is complete and on this invoice.`,
                    )
                  }
                >
                  Mark complete
                  <span className="sr-only">
                    {' '}
                    {v.customer_name}, {day(v.scheduled_start)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
      {held.length > 0 && (
        <details className="run-fold">
          <summary>{plural(held.length, 'visit')} held back</summary>
          <p className="muted">Kept off invoices until you release them.</p>
          <ul className="plain">
            {held.map((v) => (
              <li key={v.id} className="row start">
                <Link to={`/visits/${v.id}`}>
                  {day(v.scheduled_start)}, {v.customer_name}
                </Link>
                <button
                  type="button"
                  className="quiet"
                  onClick={() =>
                    void act(
                      () => post(`/api/visits/${v.id}/release`),
                      `${v.customer_name ?? 'The visit'}, ${day(v.scheduled_start)}, is released and on this invoice.`,
                    )
                  }
                >
                  Release
                  <span className="sr-only">
                    {' '}
                    {v.customer_name}, {day(v.scheduled_start)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}

      {p && (
        <div className="run-bar">
          <span aria-live="polite">
            {plural(picked.length, 'visit')} · {formatMoney(net / 100)} + GST{' '}
            {formatMoney(gst / 100)} = <strong>{formatMoney((net + gst) / 100)}</strong>
          </span>
          <button
            type="button"
            disabled={busy || props.busy || picked.length === 0}
            onClick={() => void create()}
          >
            {busy ? 'Creating…' : 'Create invoice'}
          </button>
        </div>
      )}
    </section>
  );
}
